import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { PermissionGate } from "@/components/common/PermissionGate";
import { usePermissions } from "@/hooks/usePermissions";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Filter,
  Layers,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import {
  Field,
  SectionLabel,
  UnitInput,
} from "@/components/product-lines/FormField";
import { ProductLineTable } from "@/components/product-lines/ProductLineTable";
import { ColumnSettingsMenu } from "@/components/common/ColumnSettingsMenu";
import { BusyOverlay } from "@/components/common/BusyOverlay";
import {
  useProductLines,
  useProductLine,
  useCreateProductLine,
  useUpdateProductLine,
  useDeleteProductLine,
} from "@/hooks/useProductLines";
import { getPaginationItems } from "@/lib/pagination";
import {
  PRODUCT_LINE_STATUS_OPTIONS,
  useProductLineFiltersStore,
  type ProductLineListFilters,
} from "@/stores/productLineFiltersStore";
import {
  PRODUCT_LINE_COLUMNS,
  useProductLineColumnsStore,
} from "@/stores/productLineColumnsStore";
import {
  PRODUCT_LINE_TIERS,
  type ProductLineListDto,
  type CreateProductLineRequest,
  type UpdateProductLineRequest,
} from "@/types/product-line";

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function ProductLineListPage() {
  // Catalog writes are SuperAdmin+BillingAdmin; the listing itself reads fine for all.
  const { hasRole } = usePermissions();
  const canEditCatalog = hasRole(["SuperAdmin", "BillingAdmin"]);

  const { search, status } = useProductLineFiltersStore(
    (state) => state.filters,
  );
  const page = useProductLineFiltersStore((state) => state.page);
  const pageSize = useProductLineFiltersStore((state) => state.pageSize);
  const setFilter = useProductLineFiltersStore((state) => state.setFilter);
  const setPage = useProductLineFiltersStore((state) => state.setPage);
  const setPageSize = useProductLineFiltersStore((state) => state.setPageSize);
  const clearCache = useProductLineFiltersStore((state) => state.clearCache);
  const hasActiveFilters = useProductLineFiltersStore((state) =>
    state.hasActiveFilters(),
  );

  const hiddenColumns = useProductLineColumnsStore((state) => state.hidden);
  const toggleColumn = useProductLineColumnsStore((state) => state.toggle);
  const showAllColumns = useProductLineColumnsStore((state) => state.showAll);

  // Only the half-typed term is local: committing per keystroke would re-query and write
  // sessionStorage per character.
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    const timer = setTimeout(() => {
      // Guarded because this also runs on mount, where committing the restored value would be a
      // no-op write that still sends the operator back to page 1.
      if (searchInput !== search) {
        setFilter("search", searchInput);
      }
    }, 750);
    return () => clearTimeout(timer);
  }, [searchInput, search, setFilter]);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProductLineListDto | null>(
    null,
  );
  const [deactivateTarget, setDeactivateTarget] =
    useState<ProductLineListDto | null>(null);

  const {
    data: response,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useProductLines({
    search: search || undefined,
    isActive: status === "All" ? undefined : status === "Active",
    page,
    limit: pageSize,
  });
  const createMutation = useCreateProductLine();
  const updateMutation = useUpdateProductLine();
  const deleteMutation = useDeleteProductLine();

  // A term typed but not yet committed: nothing is fetching yet, so the search box says so itself.
  const isSearchPending = searchInput !== search;

  // A catalog write covers the table's Card, not the page: the table must not take a second write
  // while this one settles, and the rest of the console stays usable.
  let busyLabel: string | null = null;
  if (createMutation.isPending) {
    busyLabel = "Creating product line…";
  } else if (updateMutation.isPending) {
    busyLabel = "Updating product line…";
  } else if (deleteMutation.isPending) {
    busyLabel = "Deleting product line…";
  }

  const productLines = response?.data || [];
  const totalPages = response?.pagination?.totalPages || 1;
  const currentPage = page;
  const totalItems = response?.pagination?.totalItems || 0;

  // Used by both the loading and the loaded branch: the table owns its skeleton rows.
  const productLineTable = (
    <ProductLineTable
      productLines={productLines}
      isLoading={isLoading}
      canEdit={canEditCatalog}
      isUpdating={updateMutation.isPending}
      onActivate={(productLine) =>
        updateMutation.mutate({
          id: productLine.id,
          data: { isActive: true },
        })
      }
      onDeactivate={setDeactivateTarget}
      onDelete={setDeleteTarget}
    />
  );

  // The screen's "start over": drops every filter, the page size and the cached copy, then
  // reloads. The search box holds its own copy of the term, so it is cleared too.
  const handleRefresh = () => {
    setSearchInput("");
    clearCache();
    void refetch();
  };

  // Take the screen over only when there is nothing to show; with rows in hand a failed refetch is
  // a banner above the table, so the filter bar survives.
  if (error && !response) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-destructive-ink">
                Failed to load product lines
              </p>
              <Button onClick={() => refetch()} className="mt-4">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col space-y-2">
      <Card className="relative flex min-h-0 flex-1 flex-col shadow-none">
        {/* Anchored to the card, and never alongside the skeleton: skeleton while there is nothing
            to show, the pill only while refreshing rows the operator can still see. */}
        {isFetching && !isLoading && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
            <div className="flex items-center gap-2 rounded-lg border bg-background px-4 py-2 shadow-lg">
              <RefreshCw className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Updating...</span>
            </div>
          </div>
        )}

        <BusyOverlay
          contained
          open={busyLabel !== null}
          label={busyLabel ?? ""}
        />

        <CardHeader className="shrink-0 p-2">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-3 sm:flex-nowrap sm:items-end">
            <div className="w-full sm:w-[360px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                {isSearchPending && (
                  <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                )}
                <Input
                  placeholder="Search name or slug"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  // React withholds onChange while an IME composition is open (Vietnamese
                  // Telex/VNI), so the closing value is taken here too.
                  onCompositionEnd={(e) =>
                    setSearchInput(e.currentTarget.value)
                  }
                  className="pl-10 pr-9"
                />
              </div>
            </div>

            {/* The screen's own action sits right after the search box, not at the far end. */}
            <PermissionGate roles={["SuperAdmin", "BillingAdmin"]}>
              <Button
                onClick={() => setIsFormOpen(true)}
                size="sm"
                className="h-8 min-h-8 shrink-0 gap-2 px-2 text-sm"
              >
                <Plus className="h-4 w-4" />
                New Product Line
              </Button>
            </PermissionGate>

            <div className="order-3 flex w-full min-w-0 items-center justify-start gap-2 overflow-x-auto sm:order-none sm:w-auto sm:flex-1 sm:self-center sm:justify-end">
              {search && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Search: {search}
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput("");
                      setFilter("search", "");
                    }}
                    aria-label="Clear search filter"
                    className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {status !== "All" && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Status: {status}
                  <button
                    type="button"
                    onClick={() => setFilter("status", "All")}
                    aria-label="Clear status filter"
                    className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  title="Filters"
                  aria-label="Filters"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 space-y-4 p-4">
                <div className="space-y-2">
                  <Label htmlFor="productLineStatusFilter">Status</Label>
                  <Select
                    value={status}
                    onValueChange={(value) =>
                      setFilter(
                        "status",
                        value as ProductLineListFilters["status"],
                      )
                    }
                  >
                    <SelectTrigger id="productLineStatusFilter">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {PRODUCT_LINE_STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </PopoverContent>
            </Popover>

            <ColumnSettingsMenu
              columns={PRODUCT_LINE_COLUMNS}
              isVisible={(id) => !hiddenColumns.includes(id)}
              toggle={toggleColumn}
              showAll={showAllColumns}
              hiddenCount={hiddenColumns.length}
              className="h-8 min-h-8 px-2 hover:translate-y-0 active:translate-y-0"
            />
          </div>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col p-2 pt-0">
          {error && (
            <div className="mb-2 flex shrink-0 items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2">
              <p className="text-sm text-destructive-ink">
                Could not refresh the list. Showing the last result that loaded.
              </p>
              <Button
                onClick={() => refetch()}
                variant="outline"
                size="sm"
                disabled={isFetching}
                className="h-7 shrink-0 px-2 text-xs"
              >
                Retry
              </Button>
            </div>
          )}
          {isLoading ? (
            productLineTable
          ) : productLines.length === 0 ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
              <Layers className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">
                {hasActiveFilters
                  ? "No product lines found"
                  : "No product lines yet"}
              </h3>
              <p className="text-muted-foreground">
                {hasActiveFilters
                  ? "Try adjusting or clearing your filters."
                  : "Product lines will appear here once created."}
              </p>
            </div>
          ) : (
            productLineTable
          )}

          {/* Outside the three branches, so a zero-result search keeps its pager and page-size
              picker. Held back only on the first load, where "0 total" would be a lie. */}
          {!isLoading && (
            <div className="mt-auto flex shrink-0 flex-col items-center gap-2 pt-2 sm:flex-row sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages} ({totalItems} total)
              </p>
              <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => setPageSize(parseInt(value))}
                >
                  <SelectTrigger className="h-8 w-[120px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1 overflow-x-auto">
                  <Button
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  {getPaginationItems(currentPage, totalPages).map(
                    (item, index) =>
                      item === "ellipsis" ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-1 text-sm text-muted-foreground"
                        >
                          …
                        </span>
                      ) : (
                        <Button
                          key={item}
                          onClick={() => setPage(item)}
                          disabled={isFetching}
                          variant={currentPage === item ? "default" : "outline"}
                          size="sm"
                          className="min-w-9"
                        >
                          {item}
                        </Button>
                      ),
                  )}
                  <Button
                    onClick={() =>
                      setPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>

                {/* Icon only: this clears the filters as well as reloading, so a "Refresh"
                    caption would promise less than the click does. */}
                <Button
                  onClick={handleRefresh}
                  variant="outline"
                  size="sm"
                  disabled={isFetching}
                  title="Reset filters and reload"
                  aria-label="Reset filters and reload"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ProductLineFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        item={null}
        onSubmit={(data) =>
          createMutation.mutate(data as CreateProductLineRequest)
        }
        isSubmitting={createMutation.isPending}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product Line</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.name}"? This will
              deactivate the product line.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
                setDeleteTarget(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deactivateTarget}
        onOpenChange={() => setDeactivateTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Product Line</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Are you sure you want to deactivate &quot;{deactivateTarget?.name}
              &quot;? Products using this tier may become unavailable for new
              orders.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deactivateTarget)
                  updateMutation.mutate({
                    id: deactivateTarget.id,
                    data: { isActive: false },
                  });
                setDeactivateTarget(null);
              }}
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface FormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: ProductLineListDto | null;
  onSubmit: (data: CreateProductLineRequest | UpdateProductLineRequest) => void;
  isSubmitting: boolean;
}

function ProductLineFormDialog({
  open,
  onOpenChange,
  item,
  onSubmit,
  isSubmitting,
}: FormDialogProps) {
  const isEdit = !!item;
  const { data: detail } = useProductLine(item?.id ?? "");

  const detailData = isEdit ? detail : null;

  const [tier, setTier] = useState(item?.tier || "Standard");
  const [name, setName] = useState(item?.name || "");
  const [slug, setSlug] = useState(item?.slug || "");
  const [description, setDescription] = useState("");
  const [pricePerCpuCore, setPricePerCpuCore] = useState("0");
  const [pricePerRamGb, setPricePerRamGb] = useState("0");
  const [pricePerSsdGb, setPricePerSsdGb] = useState("0");
  const [priceFirstIp, setPriceFirstIp] = useState("0");
  const [priceAdditionalIp, setPriceAdditionalIp] = useState("0");
  const [pricePerBackupGb, setPricePerBackupGb] = useState("0");
  const [pricePerSnapshotGb, setPricePerSnapshotGb] = useState("0");
  const [defaultBackupCount, setDefaultBackupCount] = useState("2");
  const [defaultSnapshotCount, setDefaultSnapshotCount] = useState("2");
  const [sortOrder, setSortOrder] = useState("0");
  const [activeStatus, setActiveStatus] = useState(item?.isActive ?? true);

  const isDetailLoaded = !isEdit || !!detailData;

  // Populate form from detail DTO once loaded (BUG-01 fix)
  useEffect(() => {
    if (detailData) {
      setName(detailData.name);
      setSlug(detailData.slug);
      setTier(detailData.tier);
      setDescription(detailData.description ?? "");
      setSortOrder(detailData.sortOrder?.toString() ?? "0");
      setPricePerCpuCore(detailData.pricePerCpuCore?.toString() ?? "0");
      setPricePerRamGb(detailData.pricePerRamGb?.toString() ?? "0");
      setPricePerSsdGb(detailData.pricePerSsdGb?.toString() ?? "0");
      setPriceFirstIp(detailData.priceFirstIp?.toString() ?? "0");
      setPriceAdditionalIp(detailData.priceAdditionalIp?.toString() ?? "0");
      setPricePerBackupGb(detailData.pricePerBackupGb?.toString() ?? "0");
      setPricePerSnapshotGb(detailData.pricePerSnapshotGb?.toString() ?? "0");
      setDefaultBackupCount(detailData.defaultBackupCount?.toString() ?? "2");
      setDefaultSnapshotCount(
        detailData.defaultSnapshotCount?.toString() ?? "2",
      );
      setActiveStatus(detailData.isActive);
    }
  }, [detailData]);

  // Reset form when opening create dialog (BUG-05 fix)
  useEffect(() => {
    if (open && !isEdit) {
      setTier("Standard");
      setName("");
      setSlug("");
      setDescription("");
      setPricePerCpuCore("0");
      setPricePerRamGb("0");
      setPricePerSsdGb("0");
      setPriceFirstIp("0");
      setPriceAdditionalIp("0");
      setPricePerBackupGb("0");
      setPricePerSnapshotGb("0");
      setDefaultBackupCount("2");
      setDefaultSnapshotCount("2");
      setSortOrder("0");
      setActiveStatus(true);
    }
  }, [open, isEdit]);

  const handleNameChange = (value: string) => {
    setName(value);
    if (!isEdit) setSlug(slugify(value));
  };

  const handleSubmit = () => {
    if (isEdit) {
      onSubmit({
        name: name !== "" ? name : undefined,
        slug: slug !== "" ? slug : undefined,
        description: description !== "" ? description : undefined,
        pricePerCpuCore:
          pricePerCpuCore !== "" ? parseFloat(pricePerCpuCore) : undefined,
        pricePerRamGb:
          pricePerRamGb !== "" ? parseFloat(pricePerRamGb) : undefined,
        pricePerSsdGb:
          pricePerSsdGb !== "" ? parseFloat(pricePerSsdGb) : undefined,
        priceFirstIp:
          priceFirstIp !== "" ? parseFloat(priceFirstIp) : undefined,
        priceAdditionalIp:
          priceAdditionalIp !== "" ? parseFloat(priceAdditionalIp) : undefined,
        pricePerBackupGb:
          pricePerBackupGb !== "" ? parseFloat(pricePerBackupGb) : undefined,
        pricePerSnapshotGb:
          pricePerSnapshotGb !== ""
            ? parseFloat(pricePerSnapshotGb)
            : undefined,
        defaultBackupCount:
          defaultBackupCount !== "" ? parseInt(defaultBackupCount) : undefined,
        defaultSnapshotCount:
          defaultSnapshotCount !== ""
            ? parseInt(defaultSnapshotCount)
            : undefined,
        sortOrder: sortOrder !== "" ? parseInt(sortOrder) : undefined,
        isActive: activeStatus,
      });
    } else {
      onSubmit({
        tier,
        name,
        slug: slug || slugify(name),
        description: description || undefined,
        pricePerCpuCore: parseFloat(pricePerCpuCore) || 0,
        pricePerRamGb: parseFloat(pricePerRamGb) || 0,
        pricePerSsdGb: parseFloat(pricePerSsdGb) || 0,
        priceFirstIp: parseFloat(priceFirstIp) || 0,
        priceAdditionalIp: parseFloat(priceAdditionalIp) || 0,
        pricePerBackupGb: parseFloat(pricePerBackupGb) || 0,
        pricePerSnapshotGb: parseFloat(pricePerSnapshotGb) || 0,
        defaultBackupCount: parseInt(defaultBackupCount) || 2,
        defaultSnapshotCount: parseInt(defaultSnapshotCount) || 2,
        sortOrder: parseInt(sortOrder) || 0,
      });
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Product Line" : "Create Product Line"}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <DialogDescription>
            {isEdit
              ? "Update product line pricing and settings"
              : "Define a new product line tier with unit pricing"}
          </DialogDescription>
          {isEdit && !isDetailLoaded ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Loading product line details...
            </div>
          ) : (
            <>
              <section className="space-y-2.5">
                <SectionLabel>Identity</SectionLabel>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Tier">
                    <Select
                      value={tier}
                      onValueChange={setTier}
                      disabled={isEdit}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRODUCT_LINE_TIERS.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field
                    htmlFor="form-sort-order"
                    label="Sort Order"
                    hint="Lower values are listed first"
                  >
                    <Input
                      id="form-sort-order"
                      type="number"
                      className="tabular-nums"
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value)}
                    />
                  </Field>
                  <Field htmlFor="form-name" label="Name">
                    <Input
                      id="form-name"
                      value={name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      placeholder="e.g., Standard VPS"
                    />
                  </Field>
                  <Field htmlFor="form-slug" label="Slug">
                    <Input
                      id="form-slug"
                      className="font-mono"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      placeholder="auto-generated"
                    />
                  </Field>
                  <Field
                    htmlFor="form-description"
                    label="Description"
                    className="sm:col-span-2"
                  >
                    <Input
                      id="form-description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Optional description"
                    />
                  </Field>
                </div>
              </section>

              <Separator />

              <section className="space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                  <SectionLabel>Unit Pricing</SectionLabel>
                  <Badge variant="outline" className="font-mono text-xs">
                    VND
                  </Badge>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field htmlFor="form-price-cpu" label="Per CPU Core">
                    <UnitInput
                      id="form-price-cpu"
                      type="number"
                      unit="VND"
                      value={pricePerCpuCore}
                      onChange={(e) => setPricePerCpuCore(e.target.value)}
                    />
                  </Field>
                  <Field htmlFor="form-price-ram" label="Per RAM GB">
                    <UnitInput
                      id="form-price-ram"
                      type="number"
                      unit="VND"
                      value={pricePerRamGb}
                      onChange={(e) => setPricePerRamGb(e.target.value)}
                    />
                  </Field>
                  <Field htmlFor="form-price-ssd" label="Per SSD GB">
                    <UnitInput
                      id="form-price-ssd"
                      type="number"
                      unit="VND"
                      value={pricePerSsdGb}
                      onChange={(e) => setPricePerSsdGb(e.target.value)}
                    />
                  </Field>
                  <Field htmlFor="form-price-first-ip" label="First Public IP">
                    <UnitInput
                      id="form-price-first-ip"
                      type="number"
                      unit="VND"
                      value={priceFirstIp}
                      onChange={(e) => setPriceFirstIp(e.target.value)}
                    />
                  </Field>
                  <Field
                    htmlFor="form-price-additional-ip"
                    label="Each Additional IP"
                  >
                    <UnitInput
                      id="form-price-additional-ip"
                      type="number"
                      unit="VND"
                      value={priceAdditionalIp}
                      onChange={(e) => setPriceAdditionalIp(e.target.value)}
                    />
                  </Field>
                  <Field htmlFor="form-price-backup" label="Per Backup GB">
                    <UnitInput
                      id="form-price-backup"
                      type="number"
                      unit="VND"
                      value={pricePerBackupGb}
                      onChange={(e) => setPricePerBackupGb(e.target.value)}
                    />
                  </Field>
                  <Field htmlFor="form-price-snapshot" label="Per Snapshot GB">
                    <UnitInput
                      id="form-price-snapshot"
                      type="number"
                      unit="VND"
                      value={pricePerSnapshotGb}
                      onChange={(e) => setPricePerSnapshotGb(e.target.value)}
                    />
                  </Field>
                </div>
              </section>

              <Separator />

              <section className="space-y-2.5">
                <SectionLabel>Included Allowances</SectionLabel>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field
                    htmlFor="form-default-backups"
                    label="Default Backup Count"
                    hint="Included with every VM"
                  >
                    <Input
                      id="form-default-backups"
                      type="number"
                      className="tabular-nums"
                      value={defaultBackupCount}
                      onChange={(e) => setDefaultBackupCount(e.target.value)}
                    />
                  </Field>
                  <Field
                    htmlFor="form-default-snapshots"
                    label="Default Snapshot Count"
                    hint="Included with every VM"
                  >
                    <Input
                      id="form-default-snapshots"
                      type="number"
                      className="tabular-nums"
                      value={defaultSnapshotCount}
                      onChange={(e) => setDefaultSnapshotCount(e.target.value)}
                    />
                  </Field>
                </div>
              </section>

              {isEdit && (
                <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <Label className="text-sm">Active Status</Label>
                    <p className="text-xs text-muted-foreground">
                      Enable or disable this product line
                    </p>
                  </div>
                  <Switch
                    checked={activeStatus}
                    onCheckedChange={setActiveStatus}
                  />
                </div>
              )}
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || (isEdit && !isDetailLoaded)}
          >
            {isSubmitting ? "Saving..." : isEdit ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
